import { useState } from "react";
import { useAuth, claimLegacy } from "../lib/auth";
import { useFishStore, refreshFromServer } from "../lib/storage";
import { toast } from "./Toaster";
import { CloseIcon } from "../ui";

// One-time prompt for the admin to pull pre-auth data onto their account.
// Self-hiding: only shows for a signed-in admin whose collection is empty,
// so it disappears the moment the data lands (or there was none).
export function LegacyRecovery() {
  const auth = useAuth();
  const lifelistCount = useFishStore((s) => s.lifelist.length);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (auth.status !== "authed" || !auth.isAdmin) return null;
  if (lifelistCount > 0 || dismissed) return null;

  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await claimLegacy();
      if (!r.recovered) {
        toast("No earlier data found to restore.");
        setDismissed(true);
        return;
      }
      await refreshFromServer();
      const c = r.counts;
      toast(
        c
          ? `Restored ${c.lifelist} species, ${c.trips} trips.`
          : "Earlier data restored.",
      );
    } catch (e) {
      toast(e instanceof Error ? e.message : "Could not restore data.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-aqua/20 bg-aqua/5">
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-2.5">
        <p className="min-w-0 flex-1 text-sm text-ink/70">
          Have a lifelist from before sign-in existed? Restore it onto this
          account.
        </p>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          className="inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-full bg-grad-cta px-4 py-1.5 text-sm font-bold text-white shadow-card transition hover:opacity-95 disabled:opacity-60"
        >
          {busy ? "Restoring…" : "Restore my earlier data"}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-ink/35 hover:text-ink/60"
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
